/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as Yup from 'yup'
import clsx from 'clsx'
import { useFormik } from 'formik'
import { login, loginVerify, getAdminMe, mapAdminMeToProfileData } from '../core/_requests'
import { useAuth } from '../core/Auth'
import { getDashboardPath } from '../../../utils/helper'
import { KTIcon } from '../../../../_payairo/helpers'
import { generateAndSaveFCMToken } from '../../../../lib/firebase'

import { TwoFactorSetup } from './TwoFactorSetup'

const loginSchema = Yup.object().shape({
  adminId: Yup.string().min(3, 'Minimum 3 symbols'),
  // .email('Email is required'),
})

const initialValues = {
  adminId: '',
  password: '',
}

export function Login() {
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [tempToken, setTempToken] = useState<string>('')
  const [is2faEnabled, setIs2faEnabled] = useState<boolean>(false)
  const [pendingAuthData, setPendingAuthData] = useState<any>(null)

  const { saveAuth, setCurrentUser } = useAuth()
  const navigate = useNavigate()

  // Complete login after 2FA verification
  const handle2FASuccess = async (verifyResult?: any) => {
    if (!pendingAuthData && !verifyResult) {
      navigate('/dashboard', { replace: true })
      return
    }

    const authPayload = verifyResult?.data || verifyResult || pendingAuthData || {}
    const tokens = authPayload?.tokens || pendingAuthData?.tokens || {}
    const userData = (authPayload?.user || pendingAuthData?.user || authPayload) as any
    let userType = userData?.user_type || 'Admin'
    let role = userData?.role || authPayload?.role || userType
    const userId = userData?.id || authPayload?.userId || userData?.user_id
    const token = tokens?.access || authPayload?.access_token || authPayload?.token || pendingAuthData?.tokens?.access || pendingAuthData?.access_token
    const refreshToken = tokens?.refresh || authPayload?.refresh_token || pendingAuthData?.tokens?.refresh || pendingAuthData?.refresh_token

    if (token) {
      localStorage.setItem('access_token', token)
      localStorage.setItem('token', token)
    }
    if (refreshToken) {
      localStorage.setItem('refresh_token', refreshToken)
    }
    localStorage.setItem('user_type', userType)
    localStorage.setItem('user', JSON.stringify(userData))

    // Persist 2FA completion status for this email
    const userEmail = (userData?.email || userData?.username || userData?.adminId || '').toLowerCase().trim()
    if (userEmail) {
      localStorage.setItem(`2fa_enabled_${userEmail}`, 'true')
    }

    // Temporarily store token so the global interceptor uses it
    saveAuth({ access_token: token, token: token, refresh_token: refreshToken } as any)

    let profileData = userData?.profileData || authPayload?.profileData
    try {
      const payload = await getAdminMe(token)
      const adminProfile = mapAdminMeToProfileData(payload)
      Object.assign(userData, adminProfile)
      role = adminProfile.role || role
      userType = adminProfile.user_type || userType
      profileData = { ...profileData, ...adminProfile }
    } catch (e) {
      console.warn('Could not fetch getAdminMe during 2FA login', e)
    }

    const finalAuth = {
      ...authPayload,
      tokens: {
        access: token,
        refresh: refreshToken,
      },
      access_token: token,
      refresh_token: refreshToken,
      token: token,
      userId: userId,
      role: role,
      user_type: userType,
      user: {
        ...userData,
        role: role,
        user_type: userType,
        profileData: profileData,
      },
    }

    setCurrentUser(undefined)
    saveAuth(finalAuth as any)
    setCurrentUser({
      ...userData,
      role: role,
      user_type: userType,
      profileData: profileData,
    } as any)

    generateAndSaveFCMToken().catch((e) => console.warn('FCM registration failed', e))
    navigate(getDashboardPath(role, userType), { replace: true })
  }

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true)
      try {
        // Step 1: FastAPI POST /api/v1/users/admin/auth/login/ — admin JWT login
        const { data: authData } = await login(values.adminId, values.password)

        if (!authData || (authData as any).status === false) {
          setLoading(false)
          setStatus((authData as any)?.message || 'The login details are incorrect')
          setSubmitting(false)
          return
        }

        setLoading(false)
        console.log('Login Success:', authData)

        // Check if 2FA is required by backend response
        const rawAuth = authData as any
        const receivedTempToken =
          rawAuth?.temp_token ||
          rawAuth?.data?.temp_token ||
          rawAuth?.tokens?.temp_token

        const userEmail = (values.adminId || '').toLowerCase().trim()
        const stored2FAStatus = userEmail ? localStorage.getItem(`2fa_enabled_${userEmail}`) === 'true' : false

        const authStatus = (
          rawAuth?.data?.status ||
          rawAuth?.status ||
          ''
        ).toString().toUpperCase()

        const authMessage = (
          rawAuth?.message ||
          rawAuth?.data?.message ||
          ''
        ).toString().toLowerCase()

        // Decision Logic:
        // 1. SETUP_REQUIRED -> First-time 2FA Setup
        const isSetupRequired =
          authStatus === 'SETUP_REQUIRED' ||
          authMessage.includes('setup required')

        // 2. OTP_REQUIRED -> Existing 2FA User
        const isOtpRequired =
          authStatus === 'OTP_REQUIRED' ||
          authMessage.includes('otp verification required') ||
          rawAuth?.requires_2fa ||
          rawAuth?.data?.requires_2fa

        if (isSetupRequired) {
          setIs2faEnabled(false) // First time setup only: show QR code & call setup API
          setPendingAuthData(authData.data || authData)
          setTempToken(receivedTempToken || 'temp_token_active')
          setSubmitting(false)
          setLoading(false)
          return
        }

        if (isOtpRequired || stored2FAStatus) {
          setIs2faEnabled(true) // Existing user: NEVER call setup API, direct to OTP screen
          setPendingAuthData(authData.data || authData)
          setTempToken(receivedTempToken || 'temp_token_active')
          setSubmitting(false)
          setLoading(false)
          return
        }

        // Standard Login Flow
        const authDataObj = (authData.data || {}) as any
        const userData = (authDataObj.user || authDataObj) as any
        let userType = userData?.user_type || 'Admin'
        let role = userData?.role || authDataObj.role || userType
        const userId = userData?.id || authDataObj.userId || userData?.user_id
        const token = authDataObj.access_token || authDataObj.token
        let profileData = userData?.profileData || authDataObj.profileData

        // Store in localStorage as requested (crucial before calling getAdminMe)
        localStorage.setItem('access_token', token || '')
        localStorage.setItem('token', token || '')
        localStorage.setItem('refresh_token', authDataObj.refresh_token || '')

        // Temporarily store token so the global interceptor uses it
        saveAuth({ access_token: token, token: token, refresh_token: authDataObj.refresh_token } as any)

        // Fetch fresh role data to ensure SUPER_ADMIN flags are present without a refresh
        try {
          const payload = await getAdminMe(token)
          const adminProfile = mapAdminMeToProfileData(payload)
          Object.assign(userData, adminProfile)
          role = adminProfile.role || role
          userType = adminProfile.user_type || userType
          profileData = { ...profileData, ...adminProfile }
        } catch (e) {
          console.warn('Could not fetch getAdminMe during login', e)
        }

        localStorage.setItem('user_type', userType)
        localStorage.setItem('user', JSON.stringify(userData))

        // Store complete auth data with role-based fields
        const finalAuth = {
          ...authDataObj,
          token: token,
          userId: userId,
          role: role,
          user_type: userType,
          profileData: profileData,
          user: {
            ...userData,
            role: role,
            user_type: userType,
            profileData: profileData,
          },
        }

        // Clear previous user state before setting new auth - prevents role flicker
        setCurrentUser(undefined)
        saveAuth(finalAuth as any)
        setCurrentUser({
          ...userData,
          role: role,
          user_type: userType,
          profileData: profileData,
        } as any)

        // ✅ FCM Token generation
        try {
          await generateAndSaveFCMToken()
        } catch (e) {
          console.warn('FCM registration failed', e)
        }

        navigate(getDashboardPath(role, userType), { replace: true })
      } catch (error) {
        console.error(error)
        saveAuth(undefined)
        setStatus('The login details are incorrect')
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  const theme = localStorage.getItem('kt_theme_mode_value')

  // Extract initial method from pending auth data or raw response if available
  const initialMethod =
    pendingAuthData?.method ||
    pendingAuthData?.data?.method ||
    (pendingAuthData as any)?.rawAuth?.data?.method ||
    null

  // If 2FA Step is Active, render TwoFactorSetup component
  if (tempToken) {
    return (
      <TwoFactorSetup
        tempToken={tempToken}
        userEmail={formik.values.adminId}
        is2faEnabled={is2faEnabled}
        initialMethod={initialMethod}
        onSuccess={handle2FASuccess}
        onCancel={() => {
          setTempToken('')
          setIs2faEnabled(false)
          setPendingAuthData(null)
        }}
      />
    )
  }

  return (
    <form
      className='form w-100'
      onSubmit={formik.handleSubmit}
      noValidate
      id='kt_login_signin_form'
    >
      {/* begin::Heading */}
      <div className='text-center mb-11'>
        {theme == 'light' ? (
          <img src='/media/logos/payairo-color.png' className='w-100' />
        ) : (
          <img src='/media/logos/payairo-white.png' className='w-100' />
        )}
      </div>

      <div className='text-center mb-11'>
        <h1 className='text-dark fw-bolder mb-3'>Sign In</h1>
        <div className='text-gray-500 fw-semibold fs-6'>Please enter the admin ID to login</div>
      </div>
      {/* begin::Heading */}

      {formik.status ? (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{formik.status}</div>
        </div>
      ) : (
        ''
      )}

      {/* begin::Form group */}
      <div className='fv-row mb-8'>
        <label className='form-label fs-6 fw-bolder text-dark'>Email</label>
        <div className='position-relative'>
          <input
            placeholder='Email'
            {...formik.getFieldProps('adminId')}
            className={clsx(
              'form-control bg-transparent pe-10',
              { 'is-invalid': formik.touched.adminId && formik.errors.adminId },
              {
                'is-valid': formik.touched.adminId && !formik.errors.adminId,
              }
            )}
            type='text'
            name='adminId'
          />
        </div>
        {formik.touched.adminId && formik.errors.adminId && (
          <div className='fv-plugins-message-container text-danger'>
            <span role='alert'>{formik.errors.adminId}</span>
          </div>
        )}
      </div>

      <div className='fv-row mb-3'>
        <label className='form-label fs-6 fw-bolder text-dark'>Password</label>
        <div className='position-relative'>
          <input
            placeholder='Password'
            {...formik.getFieldProps('password')}
            className={clsx(
              'form-control bg-transparent pe-15',
              { 'is-invalid': formik.touched.password && formik.errors.password },
              {
                'is-valid': formik.touched.password && !formik.errors.password,
              }
            )}
            style={{ backgroundImage: 'none' }}
            type={showPassword ? 'text' : 'password'}
            name='password'
            autoComplete='off'
          />
          <div className='position-absolute top-50 end-0 translate-middle-y d-flex align-items-center gap-2 me-3'>
            {formik.touched.password && !formik.errors.password && (
              <KTIcon iconName='check-circle' className='fs-2 text-success' />
            )}
            {formik.touched.password && formik.errors.password && (
              <KTIcon iconName='cross-circle' className='fs-2 text-danger' />
            )}
            <button
              type='button'
              className='btn btn-sm btn-icon'
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              tabIndex={-1}
            >
              <KTIcon
                iconName={showPassword ? 'eye-slash' : 'eye'}
                className='fs-2 text-gray-500'
              />
            </button>
          </div>
        </div>
        {formik.touched.password && formik.errors.password && (
          <div className='fv-plugins-message-container text-danger'>
            <span role='alert'>{formik.errors.password}</span>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Action */}
      <div className='d-grid mb-10'>
        <button
          type='submit'
          id='kt_sign_in_submit'
          className='btn btn-primary'
          disabled={formik.isSubmitting || !formik.isValid}
        >
          {!loading && <span className='indicator-label'>Login</span>}
          {loading && (
            <span className='indicator-progress' style={{ display: 'block' }}>
              Please wait...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>
      {/* end::Action */}

      <div className='text-gray-500 text-center fw-semibold fs-6'>
        Need any help ?{' '}
        <a href='https://payairo.com/contactus.html' className='link-primary' target='_blank'>
          Contact Us
        </a>
      </div>
    </form>
  )
}
